import React, { createContext, useContext, useEffect, useState } from 'react';
import { get_color_scheme_api } from '../DAL/ColorScheme/ColorScheme';
import { login_public_branding_api } from '../DAL/general_routes/general_routes';
import { getIdToken } from '@xinosolutions/xs-auth-react';

const ThemeContext = createContext(undefined);
const DEFAULT_THEME_PRIMARY = '#2a9988';
const DEFAULT_DARK_BACKGROUND = 'rgba(9, 35, 41, 1)';
const DEFAULT_DARK_SECONDARY = '#0f2e36';
const DEFAULT_DARK_TEXT = '#90a7ad';
const DEFAULT_PUBLIC_BRANDING = {
  brand_name: "XS Learnix",
  meta_title: "XS Learnix",
  meta_decription: "Sign in to continue to your portal",
};

function readCachedColorScheme() {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem('color_scheme');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.theme_primary || parsed?.theme_secondary) return parsed;
  } catch (e) {
    console.error('Failed to parse cached color scheme', e);
  }
  return null;
}

function customColorsFromScheme(color_scheme) {
  if (!color_scheme) {
    return {
      theme_primary: DEFAULT_THEME_PRIMARY,
      background_primary: DEFAULT_DARK_BACKGROUND,
      theme_secondary: DEFAULT_DARK_SECONDARY,
      text_primary: DEFAULT_DARK_TEXT,
    };
  }
  return {
    theme_primary: color_scheme.theme_primary || DEFAULT_THEME_PRIMARY,
    background_primary: color_scheme.background_primary || DEFAULT_DARK_BACKGROUND,
    theme_secondary: color_scheme.theme_secondary || DEFAULT_DARK_SECONDARY,
    text_primary: color_scheme.text_primary || DEFAULT_DARK_TEXT,
    ...(color_scheme.border_color && { border_color: color_scheme.border_color }),
    ...(color_scheme.theme_light && { theme_light: color_scheme.theme_light }),
  };
}

/** Applies saved color_scheme to document root (dark vs light handling matches theme toggle). */
export function applyColorSchemeToRoot(color_scheme, root = document.documentElement) {
  if (!color_scheme) return;
  const { background_primary, text_primary, theme_primary, theme_secondary, border_color } =
    color_scheme;
  if (theme_primary) root.style.setProperty('--portal-theme-color', theme_primary);
  const activeTheme =
    localStorage.getItem('theme') ||
    (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light');
  if (activeTheme === 'dark') {
    if (background_primary) root.style.setProperty('--portal-theme-primary', background_primary);
    if (text_primary) root.style.setProperty('--text-primary-color', text_primary);
    if (theme_secondary) {
      root.style.setProperty('--portal-theme-secondary', theme_secondary);
      if (color_scheme.theme_light) {
        root.style.setProperty('--portal-theme-light', color_scheme.theme_light);
      } else {
        root.style.setProperty(
          '--portal-theme-light',
          `color-mix(in srgb, ${theme_secondary} 96%, white)`
        );
      }
    }
    if (border_color) root.style.setProperty('--input-border-color', border_color);
  } else {
    root.style.removeProperty('--portal-theme-primary');
    root.style.removeProperty('--text-primary-color');
    root.style.removeProperty('--portal-theme-secondary');
    root.style.removeProperty('--portal-theme-light');
    root.style.removeProperty('--input-border-color');
  }
}

function clearColorSchemeFromRoot(root = document.documentElement) {
  root.style.removeProperty('--portal-theme-color');
  root.style.removeProperty('--portal-theme-primary');
  root.style.removeProperty('--text-primary-color');
  root.style.removeProperty('--portal-theme-secondary');
  root.style.removeProperty('--portal-theme-light');
  root.style.removeProperty('--input-border-color');
}

function applyCachedColorScheme(root = document.documentElement) {
  const cached = readCachedColorScheme();
  if (cached) applyColorSchemeToRoot(cached, root);
  return cached;
}

function resetToDefaultColors() {
  localStorage.removeItem('color_scheme');
  clearColorSchemeFromRoot();
  return customColorsFromScheme(null);
}

export function ThemeProvider({ children, defaultTheme = 'dark' }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme');
      if (stored === 'dark' || stored === 'light') {
        return stored;
      }
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
      return 'light';
    }
    return defaultTheme;
  });
  
  const [mounted, setMounted] = useState(false);

  const [customColors, setCustomColors] = useState(() => {
    const cached = readCachedColorScheme();
    return customColorsFromScheme(cached);
  });

  const [publicPortalSetting, setPublicPortalSetting] = useState(null);
  const [publicBrandingReady, setPublicBrandingReady] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    
    localStorage.setItem('theme', theme);
    applyCachedColorScheme(root);
  }, [theme]);

  useEffect(() => {
    applyCachedColorScheme();

    const fetchPublicBranding = async (cached, clearAssociateCache = false) => {
      setPublicBrandingReady(false);
      if (clearAssociateCache) {
        localStorage.removeItem('color_scheme');
      }

      try {
        const result = await login_public_branding_api();
        if (result?.code === 200) {
          setPublicPortalSetting({
            ...DEFAULT_PUBLIC_BRANDING,
            ...(result.portal_setting || {}),
          });
          if (result.color_scheme) {
            localStorage.setItem('color_scheme', JSON.stringify(result.color_scheme));
            applyColorSchemeToRoot(result.color_scheme);
            setCustomColors(customColorsFromScheme(result.color_scheme));
            return;
          }

          if (!clearAssociateCache && cached) {
            applyColorSchemeToRoot(cached);
            setCustomColors(customColorsFromScheme(cached));
            return;
          }

          setCustomColors(resetToDefaultColors());
          return;
        }

        if (!clearAssociateCache && cached) {
          applyColorSchemeToRoot(cached);
          setCustomColors(customColorsFromScheme(cached));
          return;
        }

        setPublicPortalSetting({ ...DEFAULT_PUBLIC_BRANDING });
        setCustomColors(resetToDefaultColors());
      } catch (error) {
        console.error('Failed to load public login branding:', error);
        if (!clearAssociateCache && cached) {
          applyColorSchemeToRoot(cached);
          setCustomColors(customColorsFromScheme(cached));
          setPublicPortalSetting({ ...DEFAULT_PUBLIC_BRANDING });
          return;
        }

        setPublicPortalSetting({ ...DEFAULT_PUBLIC_BRANDING });
        setCustomColors(resetToDefaultColors());
      } finally {
        setPublicBrandingReady(true);
      }
    };

    const fetchAuthenticatedColorScheme = async (cached) => {
      setPublicPortalSetting(null);
      try {
        const result = await get_color_scheme_api();
        if (result?.code === 200 && result.color_scheme) {
          localStorage.setItem('color_scheme', JSON.stringify(result.color_scheme));
          applyColorSchemeToRoot(result.color_scheme);
          setCustomColors(customColorsFromScheme(result.color_scheme));
          return;
        }

        if (cached) {
          applyColorSchemeToRoot(cached);
          setCustomColors(customColorsFromScheme(cached));
          return;
        }

        setCustomColors(resetToDefaultColors());
      } catch (error) {
        console.error('Failed to load color scheme:', error);
        if (cached) {
          applyColorSchemeToRoot(cached);
          setCustomColors(customColorsFromScheme(cached));
        } else {
          // Role/auth denial with no cache — fall back to public login branding.
          await fetchPublicBranding(null, false);
          return;
        }
      } finally {
        setPublicBrandingReady(true);
      }
    };

    const fetchAndApplyColorScheme = async (event) => {
      const cached = applyCachedColorScheme();
      const isLogout = event?.type === 'user-logout';

      if (getIdToken()) {
        await fetchAuthenticatedColorScheme(cached);
        return;
      }

      await fetchPublicBranding(cached, isLogout);
    };

    fetchAndApplyColorScheme();

    const handleLogin = () => fetchAndApplyColorScheme({ type: 'user-login' });
    const handleLogout = () => fetchAndApplyColorScheme({ type: 'user-logout' });

    window.addEventListener('user-login', handleLogin);
    window.addEventListener('user-logout', handleLogout);
    return () => {
      window.removeEventListener('user-login', handleLogin);
      window.removeEventListener('user-logout', handleLogout);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const stored = localStorage.getItem('theme');
      if (!stored) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mounted]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    mounted,
    customColors,
    publicPortalSetting,
    publicBrandingReady,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
